import type { Section, SiteDocument, Theme } from "./types";

export const themes: Record<string, Theme> = {
  aegean: {
    primary: "#123B35",
    secondary: "#E6F0EC",
    accent: "#EF8354",
    background: "#F7F5EF",
    surface: "#FFFFFF",
    text: "#18302B",
    muted: "#687872",
    headingFont: "Manrope",
    bodyFont: "Inter",
    radius: "soft",
    buttonStyle: "solid"
  },
  mediterranean: {
    primary: "#123A63",
    secondary: "#E8F1F8",
    accent: "#DDA15E",
    background: "#FBF8F1",
    surface: "#FFFFFF",
    text: "#102A43",
    muted: "#627D98",
    headingFont: "Playfair Display",
    bodyFont: "Manrope",
    radius: "round",
    buttonStyle: "solid"
  },
  midnight: {
    primary: "#C4B5FD", secondary: "#28253D", accent: "#67E8F9",
    background: "#13111F", surface: "#211E31", text: "#F4F2FF", muted: "#B5AECF",
    headingFont: "DM Sans", bodyFont: "Inter", radius: "round", buttonStyle: "solid",
    surfaceEffect: "glass", buttonShape: "pill", motion: "subtle"
  },
  bloom: {
    primary: "#783C57", secondary: "#F6E5EC", accent: "#A14826",
    background: "#FFF9F6", surface: "#FFFFFF", text: "#3A2631", muted: "#79626C",
    headingFont: "Playfair Display", bodyFont: "Manrope", radius: "round", buttonStyle: "solid",
    surfaceEffect: "shadow", imageShape: "arch", buttonShape: "pill"
  },
  studio: {
    primary: "#202124",
    secondary: "#EEECE7",
    accent: "#D7FF58",
    background: "#F5F2EB",
    surface: "#FFFFFF",
    text: "#1A1A1A",
    muted: "#696969",
    headingFont: "DM Sans",
    bodyFont: "DM Sans",
    radius: "sharp",
    buttonStyle: "outline"
  }
};

export const sectionCatalog: Array<{ type: Section["type"]; labelEl: string; labelEn: string; descriptionEl: string }> = [
  { type: "hero", labelEl: "Κεντρικό καλωσόρισμα", labelEn: "Hero", descriptionEl: "Τίτλος, εικόνα και βασική ενέργεια" },
  { type: "about", labelEl: "Σχετικά με εμάς", labelEn: "About", descriptionEl: "Η ιστορία και οι αξίες της επιχείρησης" },
  { type: "services", labelEl: "Υπηρεσίες", labelEn: "Services", descriptionEl: "Παρουσίαση υπηρεσιών ή προϊόντων" },
  { type: "gallery", labelEl: "Συλλογή", labelEn: "Gallery", descriptionEl: "Εικόνες από τη δουλειά ή τον χώρο σας" },
  { type: "faq", labelEl: "Συχνές ερωτήσεις", labelEn: "FAQ", descriptionEl: "Ερωτήσεις και απαντήσεις με αναπτυσσόμενα πλαίσια" },
  { type: "testimonials", labelEl: "Κριτικές", labelEn: "Testimonials", descriptionEl: "Λόγια από ευχαριστημένους πελάτες" },
  { type: "contact", labelEl: "Επικοινωνία", labelEn: "Contact", descriptionEl: "Στοιχεία, ωράριο και φόρμα" },
  { type: "cta", labelEl: "Προτροπή", labelEn: "Call to action", descriptionEl: "Ένα δυνατό μήνυμα με κουμπί" }
];

export function makeSection(type: Section["type"], id: string = crypto.randomUUID()): Section {
  const common = { id, visible: true };
  switch (type) {
    case "hero": return { ...common, type, eyebrow: "ΚΑΛΩΣ ΗΡΘΑΤΕ", title: "Η επιχείρησή σας, όπως της αξίζει να φαίνεται", subtitle: "Μια καθαρή και σύγχρονη παρουσία που βοηθά τους πελάτες σας να σας γνωρίσουν.", primaryLabel: "Επικοινωνήστε μαζί μας", primaryHref: "#contact", secondaryLabel: "Οι υπηρεσίες μας", secondaryHref: "#services", align: "left" };
    case "about": return { ...common, type, eyebrow: "Η ΙΣΤΟΡΙΑ ΜΑΣ", title: "Δίπλα σας με φροντίδα και συνέπεια", body: "Πιστεύουμε στις ανθρώπινες σχέσεις, στην καλή δουλειά και στις λεπτομέρειες που κάνουν τη διαφορά. Εδώ μπορείτε να μοιραστείτε την ιστορία της επιχείρησής σας.", stats: [{ value: "10+", label: "Χρόνια εμπειρίας" }, { value: "500+", label: "Χαρούμενοι πελάτες" }] };
    case "services": return { ...common, type, eyebrow: "ΤΙ ΠΡΟΣΦΕΡΟΥΜΕ", title: "Υπηρεσίες με ουσία", intro: "Ό,τι χρειάζεστε, με απλή διαδικασία και προσωπική εξυπηρέτηση.", items: [
      { id: crypto.randomUUID(), title: "Προσωπική εξυπηρέτηση", description: "Λύσεις προσαρμοσμένες στις πραγματικές σας ανάγκες.", icon: "sparkles" },
      { id: crypto.randomUUID(), title: "Ποιότητα", description: "Προσοχή στη λεπτομέρεια, από την αρχή ως το τέλος.", icon: "award" },
      { id: crypto.randomUUID(), title: "Αξιοπιστία", description: "Καθαρή επικοινωνία και συνέπεια σε κάθε συνεργασία.", icon: "shield" }
    ] };
    case "gallery": return { ...common, type, eyebrow: "Η ΔΟΥΛΕΙΑ ΜΑΣ", title: "Μια μικρή ματιά", images: [] };
    case "faq": return { ...common, type, eyebrow: "FAQ", title: "Συχνές ερωτήσεις", intro: "", items: [] };
    case "testimonials": return { ...common, type, eyebrow: "ΟΙ ΠΕΛΑΤΕΣ ΜΑΣ", title: "Λόγια που μας δίνουν χαρά", items: [{ id: crypto.randomUUID(), quote: "Εξαιρετική εξυπηρέτηση, επαγγελματισμός και αποτέλεσμα που ξεπέρασε τις προσδοκίες μας.", name: "Μαρία Ν.", role: "Πελάτισσα" }] };
    case "contact": return { ...common, type, eyebrow: "ΕΠΙΚΟΙΝΩΝΙΑ", title: "Ας μιλήσουμε", intro: "Είμαστε εδώ για να απαντήσουμε στις ερωτήσεις σας.", phone: "+357 99 000000", email: "hello@example.com", address: "Λευκωσία, Κύπρος", hours: "Δευ–Παρ, 09:00–18:00", showForm: true };
    case "cta": return { ...common, type, title: "Έτοιμοι να ξεκινήσουμε;", body: "Επικοινωνήστε σήμερα και ας κάνουμε την ιδέα σας πραγματικότητα.", label: "Μιλήστε μαζί μας", href: "#contact" };
  }
}

export function createStarterSite(ownerId = "demo-user", id = "demo-site"): SiteDocument {
  const hero = makeSection("hero", "hero");
  const about = makeSection("about", "about");
  const services = makeSection("services", "services");
  const contact = makeSection("contact", "contact");
  return {
    id,
    ownerId,
    name: "Kýma Studio",
    slug: "kyma-studio",
    status: "draft",
    locale: "el",
    theme: themes.aegean!,
    navigation: [
      { id: "nav-home", label: "Αρχική", href: "/" },
      { id: "nav-about", label: "Σχετικά", href: "#about" },
      { id: "nav-services", label: "Υπηρεσίες", href: "#services" },
      { id: "nav-contact", label: "Επικοινωνία", href: "#contact" }
    ],
    pages: [{
      id: "home",
      slug: "",
      title: "Αρχική",
      seoTitle: "Kýma Studio | Δημιουργικές υπηρεσίες στην Κύπρο",
      seoDescription: "Προσωπικές, ποιοτικές υπηρεσίες από το Kýma Studio.",
      showInNavigation: true,
      showInFooter: true,
      sections: [hero, about, services, contact]
    }],
    social: { instagram: "https://instagram.com/" }
  };
}
